import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { examActions } from '../../redux/actions';
import { AppDispatch } from '../../store';
import styled from 'styled-components';
import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Packer, Table, TableRow, TableCell, WidthType, BorderStyle, UnderlineType } from 'docx';
import { saveAs } from 'file-saver';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;
const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  max-width: 1200px;
  width: 95%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 2px solid #e1e8ed;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-radius: 20px 20px 0 0;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
`;

const ViewModeSelector = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 32px;
  background: #f8f9fa;
  border-bottom: 1px solid #e1e8ed;
`;

const ViewModeButton = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)' 
    : 'white'};
  color: ${props => props.$active ? 'white' : '#657786'};
  box-shadow: ${props => props.$active ? '0 4px 12px rgba(0, 123, 255, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-left: auto;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(17, 153, 142, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(17, 153, 142, 0.4);
  }
`;

const ModalBody = styled.div`
  padding: 32px;
  overflow-y: auto;
  flex: 1;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #657786;
  
  &::before {
    content: '⏳';
    font-size: 48px;
    margin-bottom: 16px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ExamHeader = styled.div<{ $viewMode: string }>`
  margin-bottom: 32px;
  padding: ${props => props.$viewMode === 'print' ? '20px' : '24px'};
  background: ${props => props.$viewMode === 'print' 
    ? 'white' 
    : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'};
  border-radius: ${props => props.$viewMode === 'print' ? '0' : '12px'};
  border: ${props => props.$viewMode === 'print' ? '2px solid #000' : 'none'};
`;

const ExamTitle = styled.h1<{ $viewMode: string }>`
  font-size: ${props => props.$viewMode === 'print' ? '24px' : '28px'};
  font-weight: 700;
  color: ${props => props.$viewMode === 'print' ? '#000' : '#2c3e50'};
  margin: 0 0 12px 0;
  text-align: center;
  text-transform: uppercase;
`;

const ExamMeta = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
  font-size: 14px;
  color: #657786;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  strong {
    color: #2c3e50;
  }
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionCard = styled.div<{ $viewMode: string }>`
  background: ${props => props.$viewMode === 'print' ? 'white' : '#fff'};
  border: ${props => props.$viewMode === 'print' ? '1px solid #000' : '2px solid #e1e8ed'};
  border-radius: ${props => props.$viewMode === 'print' ? '0' : '12px'};
  padding: 20px;
  transition: all 0.2s ease;
  page-break-inside: avoid;

  ${props => props.$viewMode !== 'print' && `
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  `}
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const QuestionNumber = styled.div<{ $viewMode: string }>`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.$viewMode === 'print' ? '#000' : '#007bff'};
`;

const QuestionId = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #6c757d;
  margin-left: 8px;
`;

const QuestionBadges = styled.div`
  display: flex;
  gap: 8px;
`;

const Badge = styled.span<{ $type: string }>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch(props.$type) {
      case 'easy': return '#d4edda';
      case 'medium': return '#fff3cd';
      case 'hard': return '#f8d7da';
      case 'type': return '#d1ecf1';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch(props.$type) {
      case 'easy': return '#155724';
      case 'medium': return '#856404';
      case 'hard': return '#721c24';
      case 'type': return '#0c5460';
      default: return '#383d41';
    }
  }};
`;

const QuestionContent = styled.div<{ $viewMode: string }>`
  font-size: 16px;
  line-height: 1.6;
  color: ${props => props.$viewMode === 'print' ? '#000' : '#2c3e50'};
  margin-bottom: 16px;
  white-space: pre-wrap;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-left: 24px;
`;

const OptionItem = styled.div<{ $isCorrect?: boolean; $viewMode: string }>`
  padding: 12px;
  border-radius: ${props => props.$viewMode === 'print' ? '0' : '8px'};
  background: ${props => {
    if (props.$viewMode === 'print') return 'transparent';
    if (props.$isCorrect) return '#d4edda';
    return '#f8f9fa';
  }};
  border: ${props => props.$viewMode === 'print' ? 'none' : '1px solid #e1e8ed'};
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  color: ${props => props.$viewMode === 'print' ? '#000' : '#2c3e50'};

  ${props => props.$isCorrect && props.$viewMode === 'teacher' && `
    border-left: 4px solid #28a745;
    font-weight: 600;
  `}
`;

const OptionLabel = styled.span`
  font-weight: 700;
  min-width: 24px;
`;

const CorrectAnswerSection = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: #d4edda;
  border-left: 4px solid #28a745;
  border-radius: 8px;
  font-size: 14px;
  
  strong {
    color: #155724;
  }
`;

const PrintStyles = styled.div`
  @media print {
    body * {
      visibility: hidden;
    }
    
    #print-content, #print-content * {
      visibility: visible;
    }
    
    #print-content {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
  }
`;

interface ExamPreviewModalProps {
  examId: number;
  onClose: () => void;
}

type ViewMode = 'student' | 'teacher' | 'print';

const ExamPreviewModal: React.FC<ExamPreviewModalProps> = ({ examId, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [exam, setExam] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('student');
  const printContentRef = useRef<HTMLDivElement>(null);

  const groupedExamQuestions = useMemo(() => {
    if (!exam?.exam_questions) return [];
    const grouped: Record<number, { typeId: number; typeInfo: any; questions: any[] }> = {};
    exam.exam_questions.forEach((eq: any) => {
      const typeId = eq.question?.question_type_id || 0;
      if (!grouped[typeId]) {
        grouped[typeId] = { typeId, typeInfo: eq.question?.questionType, questions: [] };
      }
      grouped[typeId].questions.push(eq);
    });
    return Object.values(grouped).sort((a, b) => a.typeId - b.typeId);
  }, [exam]);

  const questionNumberMap = useMemo(() => {
    const map = new Map<number, number>();
    let counter = 1;
    groupedExamQuestions.forEach((g) => {
      g.questions.forEach((eq) => {
        if (eq.question?.id) {
          map.set(eq.question.id, counter);
          counter += 1;
        }
      });
    });
    return map;
  }, [groupedExamQuestions]);

  useEffect(() => {
    const loadExamDetails = async () => {
      try {
        setLoading(true);
        const result: any = await dispatch(examActions.getById(examId));
        console.log('ExamPreviewModal - Raw result:', result);
        
        // Check if result has data property or is the data itself
        const examData = result?.data || result;
        console.log('ExamPreviewModal - Exam data:', examData);
        
        if (examData) {
          setExam(examData);
        } else {
          console.error('No exam data found');
        }
      } catch (error) {
        console.error('Failed to load exam:', error);
      } finally {
        setLoading(false);
      }
    };

    loadExamDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  const handlePrint = () => {
    if (!exam) return;
    
    // Build HTML content similar to Word export
    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${exam.title}</title>
        <style>
          @page { 
            size: A4; 
            margin: 1cm; 
          }
          body { 
            font-family: 'Times New Roman', serif; 
            font-size: 10pt;
            line-height: 1.5;
            margin: 0;
            padding: 0;
          }
          .title {
            text-align: center;
            font-size: 16pt;
            font-weight: bold;
            margin-bottom: 12pt;
          }
          .description {
            text-align: center;
            margin-bottom: 10pt;
          }
          .info-table {
            width: 100%;
            margin-bottom: 10pt;
            border-collapse: collapse;
          }
          .info-table td {
            padding: 5pt;
            border: 1px solid #000;
          }
          .exercise-header {
            font-weight: bold;
            font-style: italic;
            font-size: 11pt;
            margin-top: 10pt;
            margin-bottom: 8pt;
          }
          .question {
            margin-bottom: 8pt;
          }
          .question-number {
            font-weight: bold;
          }
          .question-id {
            font-style: italic;
            font-size: 8pt;
            color: #666;
          }
          .option {
            margin-left: 20pt;
          }
          .answer {
            font-weight: bold;
            color: #00FF00;
          }
          .score {
            font-style: italic;
            color: #666;
            margin-top: 5pt;
          }
          .indent {
            margin-left: 20pt;
          }
          .indent2 {
            margin-left: 40pt;
          }
        </style>
      </head>
      <body>
        <div class="title">${exam.title}</div>
    `;
    
    if (exam.description) {
      htmlContent += `<div class="description">${exam.description}</div>`;
    }
    
    // Info table
    htmlContent += `
      <table class="info-table">
        <tr>
          <td width="33%">Name: _____________________</td>
          <td width="33%">Class: _____________________</td>
          <td width="34%">Mark: _____________________</td>
        </tr>
      </table>
    `;
    
    // Group questions by type
    const groupedQuestions: { [key: number]: any[] } = {};
    exam.exam_questions?.forEach((eq: any) => {
      const typeId = eq.question.question_type_id;
      if (!groupedQuestions[typeId]) {
        groupedQuestions[typeId] = [];
      }
      groupedQuestions[typeId].push(eq);
    });
    
    let exerciseNum = 1;
    let globalQuestionNum = 1;
    
    Object.keys(groupedQuestions).forEach((typeId: any) => {
      const typeIdNum = parseInt(typeId);
      const questionsInExercise = groupedQuestions[typeIdNum];
      const firstQuestion = questionsInExercise[0].question;
      const typeInfo = firstQuestion.questionType;
      
      const engTitle = typeInfo?.eng_title || '';
      const vnTitle = typeInfo?.vn_title || '';
      const headerText = `Exercise ${exerciseNum}: ${engTitle}${vnTitle ? ` (${vnTitle})` : ''}`;
      
      htmlContent += `<div class="exercise-header">${headerText}</div>`;
      
      questionsInExercise.forEach((eq: any) => {
        const q = eq.question;
        let content = q.content;
        
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = { text: content };
          }
        }
        
        htmlContent += `<div class="question">`;
        htmlContent += `<span class="question-number">${globalQuestionNum}. </span>`;
        
        // Question text based on type
        switch(typeIdNum) {
          case 1: // Multiple Choice
            htmlContent += `${content.text || ''} <span class="question-id">[${q.id}]</span><br>`;
            if (content.options) {
              const layout = calculateAnswerLayout(content.options);
              if (layout.columns === 4) {
                htmlContent += content.options.map((opt: string, i: number) => {
                  const label = String.fromCharCode(65 + i);
                  const isCorrect = viewMode === 'teacher' && content.correct_index === i;
                  return `<span class="${isCorrect ? 'answer' : ''}">${label}. ${opt}${isCorrect ? ' ✓' : ''}</span>`;
                }).join('&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;');
              } else if (layout.columns === 2) {
                for (let i = 0; i < content.options.length; i += 2) {
                  const label1 = String.fromCharCode(65 + i);
                  const isCorrect1 = viewMode === 'teacher' && content.correct_index === i;
                  htmlContent += `<div class="option"><span class="${isCorrect1 ? 'answer' : ''}">${label1}. ${content.options[i]}${isCorrect1 ? ' ✓' : ''}</span>`;
                  if (i + 1 < content.options.length) {
                    const label2 = String.fromCharCode(65 + i + 1);
                    const isCorrect2 = viewMode === 'teacher' && content.correct_index === i + 1;
                    htmlContent += `&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span class="${isCorrect2 ? 'answer' : ''}">${label2}. ${content.options[i + 1]}${isCorrect2 ? ' ✓' : ''}</span>`;
                  }
                  htmlContent += `</div>`;
                }
              } else {
                content.options.forEach((opt: string, i: number) => {
                  const label = String.fromCharCode(65 + i);
                  const isCorrect = viewMode === 'teacher' && content.correct_index === i;
                  htmlContent += `<div class="option"><span class="${isCorrect ? 'answer' : ''}">${label}. ${opt}${isCorrect ? ' ✓' : ''}</span></div>`;
                });
              }
            }
            break;
            
          case 2: // Verb Conjugation
          case 13: // Word Form
            htmlContent += `${content.text || ''} <span class="question-id">[${q.id}]</span>`;
            if (viewMode === 'teacher' && content.correct_answer) {
              htmlContent += `<br><span class="answer">Đáp án: ${content.correct_answer}</span>`;
            }
            break;
            
          case 3: // Sentence Ordering
            const words = content.words || [];
            const wordCount3 = words.length;
            htmlContent += `${words.join(' / ')} <span class="question-id">[${q.id}]</span>`;
            if (wordCount3 < 11) {
              htmlContent += ` → .................................................................`;
            } else {
              htmlContent += `<br>→ .................................................................`;
            }
            if (viewMode === 'teacher') {
              if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                htmlContent += `<br><span class="answer">Đáp án: ${content.correct_answers.join(' | ')}</span>`;
              } else if (content.correct_sentence) {
                htmlContent += `<br><span class="answer">Đáp án: ${content.correct_sentence}</span>`;
              }
            }
            break;
            
          case 4: // Write Sentence
            if (content.prompts && content.prompts.length > 0) {
              const firstPrompt = content.prompts[0].text;
              const wordCount4 = firstPrompt.includes('/') 
                ? firstPrompt.split('/').filter((w: string) => w.trim().length > 0).length
                : firstPrompt.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
              
              htmlContent += `${firstPrompt} <span class="question-id">[${q.id}]</span>`;
              
              if (wordCount4 < 11) {
                htmlContent += ` → .................................................................`;
              } else {
                htmlContent += `<br>→ .................................................................`;
              }
              
              // Remaining prompts
              content.prompts.slice(1).forEach((prompt: any) => {
                htmlContent += `<div class="indent">${prompt.text}</div>`;
              });
              
              if (viewMode === 'teacher') {
                content.prompts.forEach((prompt: any) => {
                  if (prompt.answers && Array.isArray(prompt.answers) && prompt.answers.length > 0) {
                    const answers = prompt.answers.filter(Boolean).join(' / ');
                    htmlContent += `<div class="indent"><span class="answer">Đáp án: ${answers}</span></div>`;
                  } else if (prompt.answer) {
                    htmlContent += `<div class="indent"><span class="answer">Đáp án: ${prompt.answer}</span></div>`;
                  }
                });
              }
            }
            break;
            
          case 5: // Rewrite Sentence
            htmlContent += `${content.original_sentence || ''} <span class="question-id">[${q.id}]</span>`;
            if (content.instruction) {
              htmlContent += `<br>→ ${content.instruction} ....................................................................`;
            }
            if (viewMode === 'teacher') {
              if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                htmlContent += `<br><span class="answer">Đáp án: ${content.correct_answers.join(' | ')}</span>`;
              } else if (content.correct_answer) {
                htmlContent += `<br><span class="answer">Đáp án: ${content.correct_answer}</span>`;
              }
            }
            break;
            
          case 6: // Reading Essay
          case 7: // Reading MCQ
            htmlContent += `${content.passage || ''} <span class="question-id">[${q.id}]</span>`;
            if (content.questions) {
              content.questions.forEach((subQ: any, i: number) => {
                const questionText = `${String.fromCharCode(97 + i)}) ${subQ.question_text}`;
                htmlContent += `<div class="indent">${questionText}`;
                
                if (typeIdNum === 6) {
                  const wordCount = questionText.split(/\s+/).length;
                  if (wordCount < 11) {
                    htmlContent += ` ................................................................`;
                  } else {
                    htmlContent += `<br><div class="indent">................................................................</div>`;
                  }
                  // Add correct answers for Type 6 in teacher mode
                  if (viewMode === 'teacher' && subQ.correct_answers && subQ.correct_answers.length > 0) {
                    const answers = subQ.correct_answers.filter((a: string) => a.trim()).join(' / ');
                    htmlContent += `<br><span class="answer">Đáp án: ${answers}</span>`;
                  }
                } else if (typeIdNum === 7 && subQ.options) {
                  const layout = calculateAnswerLayout(subQ.options);
                  htmlContent += `<br>`;
                  if (layout.columns === 4) {
                    htmlContent += subQ.options.map((opt: string, j: number) => {
                      const label = String.fromCharCode(65 + j);
                      const isCorrect = viewMode === 'teacher' && subQ.correct_index === j;
                      return `<span class="${isCorrect ? 'answer' : ''}">${label}. ${opt}${isCorrect ? ' ✓' : ''}</span>`;
                    }).join('&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;');
                  } else if (layout.columns === 2) {
                    for (let j = 0; j < subQ.options.length; j += 2) {
                      const label1 = String.fromCharCode(65 + j);
                      const isCorrect1 = viewMode === 'teacher' && subQ.correct_index === j;
                      htmlContent += `<div class="indent2"><span class="${isCorrect1 ? 'answer' : ''}">${label1}. ${subQ.options[j]}${isCorrect1 ? ' ✓' : ''}</span>`;
                      if (j + 1 < subQ.options.length) {
                        const label2 = String.fromCharCode(65 + j + 1);
                        const isCorrect2 = viewMode === 'teacher' && subQ.correct_index === j + 1;
                        htmlContent += `&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span class="${isCorrect2 ? 'answer' : ''}">${label2}. ${subQ.options[j + 1]}${isCorrect2 ? ' ✓' : ''}</span>`;
                      }
                      htmlContent += `</div>`;
                    }
                  } else {
                    subQ.options.forEach((opt: string, j: number) => {
                      const label = String.fromCharCode(65 + j);
                      const isCorrect = viewMode === 'teacher' && subQ.correct_index === j;
                      htmlContent += `<div class="indent2"><span class="${isCorrect ? 'answer' : ''}">${label}. ${opt}${isCorrect ? ' ✓' : ''}</span></div>`;
                    });
                  }
                }
                htmlContent += `</div>`;
              });
            }
            break;
            
          case 8: // Fill Blanks Word Bank
            if (content.word_bank) {
              htmlContent += `<strong>📦 Word Bank:</strong> ${content.word_bank.join(' / ')}<br>`;
            }
            htmlContent += `${content.text || ''} <span class="question-id">[${q.id}]</span>`;
            if (viewMode === 'teacher' && content.answers) {
              htmlContent += `<br><span class="answer">Đáp án: ${content.answers.join(', ')}</span>`;
            }
            break;
            
          case 10: // Find Error
            htmlContent += `Tìm và sửa lỗi sai trong câu: <span class="question-id">[${q.id}]</span><br>`;
            htmlContent += `${content.sentence || ''}`;
            if (viewMode === 'teacher') {
              if (content.error_word) {
                htmlContent += `<br><strong>Lỗi sai:</strong> <span style="color: #FF0000;">${content.error_word}</span>`;
              }
              if (content.correction) {
                htmlContent += `<br><span class="answer">Sửa thành: ${content.correction}</span>`;
              }
            }
            break;
            
          case 11: { // Make Question
            const sentence = content.sentence || '';
            const underlined = content.underlined || '';
            if (sentence && underlined && sentence.includes(underlined)) {
              const parts = sentence.split(underlined);
              htmlContent += `${parts[0]}<u style="font-weight:bold;">${underlined}</u>${parts[1] || ''}`;
            } else {
              htmlContent += sentence;
            }
            htmlContent += ` <span class="question-id">[${q.id}]</span>`;
            
            const wordCount = sentence.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
            if (wordCount < 11) {
              htmlContent += ` → .................................................................`;
            } else {
              htmlContent += `<br>→ .................................................................`;
            }
            
            if (viewMode === 'teacher') {
              if (content.correct_questions && Array.isArray(content.correct_questions) && content.correct_questions.length > 0) {
                htmlContent += `<br><span class="answer">Đáp án: ${content.correct_questions.join(' | ')}</span>`;
              } else if (content.correct_question) {
                htmlContent += `<br><span class="answer">Đáp án: ${content.correct_question}</span>`;
              }
            }
            break;
          }
            
          case 12: // True/False
            if (content.passage) {
              htmlContent += `<div style="font-style:italic; margin-bottom:6px;">${content.passage}</div>`;
            }
            {
              const statements = Array.isArray(content.statements) && content.statements.length
                ? content.statements
                : (content.text ? [{ text: content.text, is_true: content.is_true }] : []);
              statements.forEach((s: any, idx: number) => {
                htmlContent += `<div>${idx + 1}) ${s.text || ''} <span class="question-id">[${q.id}]</span><br>☐ True&nbsp;&nbsp;&nbsp;&nbsp;☐ False`;
                if (viewMode === 'teacher') {
                  htmlContent += `<br><span class="answer">Đáp án: ${s.is_true ? 'True' : 'False'}</span>`;
                }
                htmlContent += `</div>`;
              });
            }
            break;
            
          default:
            htmlContent += `${content.text || content.passage || JSON.stringify(content)} <span class="question-id">[${q.id}]</span>`;
        }
        
        if (viewMode === 'teacher') {
          htmlContent += `<div class="score">[${eq.score || 1} điểm]</div>`;
        }
        
        htmlContent += `</div>`;
        globalQuestionNum++;
      });
      
      exerciseNum++;
    });
    
    htmlContent += `
      </body>
      </html>
    `;
    
    // Create hidden iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    document.body.appendChild(printFrame);
    
    const doc = printFrame.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(htmlContent);
      doc.close();
      
      // Wait for content to load then print
      printFrame.contentWindow?.focus();
      setTimeout(() => {
        printFrame.contentWindow?.print();
        // Remove iframe after printing
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 100);
      }, 500);
    }
  };

  // Helper: Calculate smart layout for ABCD answers
  const calculateAnswerLayout = (options: string[]) => {
    if (!options || options.length === 0) return { columns: 1, rows: 4 };
    
    const maxLength = Math.max(...options.map(o => o.length));
    
    // 1 line: All fit in 1 line (each < 25 chars)
    if (options.length <= 4 && maxLength < 25) {
      return { columns: 4, rows: 1 };
    }
    // 2 lines: Each option < 40 chars
    if (options.length <= 4 && maxLength < 40) {
      return { columns: 2, rows: 2 };
    }
    // 4 lines (vertical)
    return { columns: 1, rows: 4 };
  };

  const handleExport = async () => {
    if (!exam) return;
    
    try {
      const children: any[] = [];
      
      // Title - Font size 16
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: exam.title,
              bold: true,
              size: 32, // 16pt = 32 half-points
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
        })
      );
      
      // Description
      if (exam.description) {
        children.push(
          new Paragraph({
            text: exam.description,
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
          })
        );
      }
      
      // Exam info table - 1 row, 3 columns (Name / Class / Mark)
      const infoTable = new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({ text: 'Name: _____________________' })],
                width: { size: 33, type: WidthType.PERCENTAGE },
              }),
              new TableCell({
                children: [new Paragraph({ text: 'Class: _____________________' })],
                width: { size: 33, type: WidthType.PERCENTAGE },
              }),
              new TableCell({
                children: [new Paragraph({ text: 'Mark: _____________________' })],
                width: { size: 34, type: WidthType.PERCENTAGE },
              }),
            ],
          }),
        ],
      });
      
      children.push(infoTable);
      // Start questions right after table (no extra spacing)
      
      // Group questions by question_type_id
      const groupedQuestions: { [key: number]: any[] } = {};
      exam.exam_questions?.forEach((eq: any) => {
        const typeId = eq.question.question_type_id;
        if (!groupedQuestions[typeId]) {
          groupedQuestions[typeId] = [];
        }
        groupedQuestions[typeId].push(eq);
      });
      
      // Process each exercise (group by type)
      let exerciseNum = 1;
      let globalQuestionNum = 1; // Global counter across all exercises
      
      Object.keys(groupedQuestions).forEach((typeId: any) => {
        const typeIdNum = parseInt(typeId);
        const questionsInExercise = groupedQuestions[typeIdNum];
        const firstQuestion = questionsInExercise[0].question;
        const typeInfo = firstQuestion.questionType;
        
        // Exercise header
        const engTitle = typeInfo?.eng_title || '';
        const vnTitle = typeInfo?.vn_title || '';
        const headerText = `Exercise ${exerciseNum}: ${engTitle}${vnTitle ? ` (${vnTitle})` : ''}`;
        
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: headerText,
                bold: true,
                italics: true,
                size: 22, // Slightly larger than body text (20) but not huge
              }),
            ],
            spacing: { before: 200, after: 150 },
          })
        );
        
        // Add questions in this exercise
        questionsInExercise.forEach((eq: any, qIndex: number) => {
          const q = eq.question;
          let content = q.content;
          
          // Parse content
          if (typeof content === 'string') {
            try {
              content = JSON.parse(content);
            } catch (e) {
              content = { text: content };
            }
          }
          
          // Question number and text on same line (global counter) - ID moved to end
          const questionTextRuns: any[] = [
            new TextRun({
              text: `${globalQuestionNum}. `,
              bold: true,
              size: 22,
            }),
          ];
          
          // Add question text to same line
          if (typeIdNum === 1 && content.text) {
            questionTextRuns.push(new TextRun({ text: content.text, size: 22 }));
          } else if (typeIdNum === 2 && content.text) {
            questionTextRuns.push(new TextRun({ text: content.text, size: 22 }));
          } else if (typeIdNum === 3 && content.words) {
            // Type 3: Sentence Ordering - add words on same line
            questionTextRuns.push(new TextRun({ text: content.words.join(' / '), size: 22 }));
          } else if (typeIdNum === 4 && content.prompts && content.prompts.length > 0) {
            // Type 4: Write Sentence with prompts - add first prompt text on same line
            questionTextRuns.push(new TextRun({ text: content.prompts[0].text, size: 22 }));
          } else if (typeIdNum === 5 && content.original_sentence) {
            questionTextRuns.push(new TextRun({ text: content.original_sentence, size: 22 }));
          } else if ((typeIdNum === 6 || typeIdNum === 7) && content.passage) {
            // Type 6/7: Add passage on same line, ID will be added at the end
            questionTextRuns.push(new TextRun({ text: content.passage, size: 22 }));
          } else if (typeIdNum === 8 && content.word_bank) {
            // Type 8: Add "Words: " and word bank on same line (italic, bold)
            // Use multiple TextRuns with explicit spacing to ensure words are separated
            questionTextRuns.push(new TextRun({ text: 'Words: ', size: 22 }));
            content.word_bank.forEach((word: string, idx: number) => {
              questionTextRuns.push(new TextRun({ 
                text: word, 
                size: 22,
                italics: true,
                bold: true
              }));
              // Add spacing between words (except after last word)
              if (idx < content.word_bank.length - 1) {
                questionTextRuns.push(new TextRun({ text: '      ', size: 22 })); // 6 spaces for wider gap
              }
            });
          } else if (typeIdNum === 10 && content.sentence) {
            questionTextRuns.push(new TextRun({ text: content.sentence, size: 22 }));
          } else if (typeIdNum === 11 && content.sentence) {
            // Type 11: Make Question - show sentence with underlined word
            const sentence = content.sentence;
            const underlined = content.underlined || '';
            if (underlined && sentence.includes(underlined)) {
              const parts = sentence.split(underlined);
              questionTextRuns.push(new TextRun({ text: parts[0], size: 22 }));
              questionTextRuns.push(new TextRun({ text: underlined, size: 22, underline: { type: UnderlineType.SINGLE }, bold: true }));
              if (parts[1]) {
                questionTextRuns.push(new TextRun({ text: parts[1], size: 22 }));
              }
            } else {
              questionTextRuns.push(new TextRun({ text: sentence, size: 22 }));
            }
            // Add question ID immediately after sentence for Type 11
            questionTextRuns.push(new TextRun({
              text: ` [${q.id}]`,
              italics: true,
              size: 16,
              color: '666666'
            }));
          } else if (typeIdNum === 12) {
            if (content.passage) {
              questionTextRuns.push(new TextRun({ text: content.passage, size: 22 }));
            } else if (Array.isArray(content.statements) && content.statements.length > 0) {
              questionTextRuns.push(new TextRun({ text: content.statements[0].text || '', size: 22 }));
            } else if (content.text) { // Legacy fallback
              questionTextRuns.push(new TextRun({ text: content.text, size: 22 }));
            }
          } else if (typeIdNum === 13 && content.text) {
            // Type 13: Word Form - same as Type 2
            questionTextRuns.push(new TextRun({ text: content.text, size: 22 }));
          }
          
          // Add question ID at end in italic, size 8pt (16 half-points), with square brackets
          // Skip ID for Type 6/7/9/11/12 as they have their own ID placement
          if (typeIdNum !== 6 && typeIdNum !== 7 && typeIdNum !== 9 && typeIdNum !== 11 && typeIdNum !== 12) {
            questionTextRuns.push(new TextRun({
              text: ` [${q.id}]`,
              italics: true,
              size: 16, // 8pt = 16 half-points
              color: '666666'
            }));
          } else if ((typeIdNum === 6 || typeIdNum === 7) && content.passage) {
            // For Type 6/7, add ID at end of passage on same line
            questionTextRuns.push(new TextRun({
              text: ` [${q.id}]`,
              italics: true,
              size: 16,
              color: '666666'
            }));
          } else if (typeIdNum === 9) {
            questionTextRuns.push(new TextRun({
              text: ` [${q.id}]`,
              italics: true,
              size: 16,
              color: '666666'
            }));
          }
          
          // For Type 3, 4 & 11: Add arrow and dots BEFORE pushing paragraph if wordCount < 11
          if (typeIdNum === 3 && content.words) {
            const wordCount3 = content.words.length;
            
            if (wordCount3 < 11) {
              // Add arrow and dots to same line
              questionTextRuns.push(new TextRun({ text: ' → ' }));
              questionTextRuns.push(new TextRun({ text: '.................................................................' }));
            }
          } else if (typeIdNum === 4 && content.prompts && content.prompts.length > 0) {
            const prompt1Text = content.prompts[0].text;
            const wordCount4 = prompt1Text.includes('/') 
              ? prompt1Text.split('/').filter((w: string) => w.trim().length > 0).length
              : prompt1Text.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
            
            if (wordCount4 < 11) {
              // Add arrow and dots to same line
              questionTextRuns.push(new TextRun({ text: ' → ' }));
              questionTextRuns.push(new TextRun({ text: '.................................................................' }));
            }
          } else if (typeIdNum === 11 && content.sentence) {
            // Type 11: Make Question - count words in sentence for arrow+dots logic
            const sentence = content.sentence;
            const wordCount11 = sentence.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
            
            if (wordCount11 < 11) {
              // Add arrow and dots to same line
              questionTextRuns.push(new TextRun({ text: ' → ' }));
              questionTextRuns.push(new TextRun({ text: '.................................................................' }));
            }
          }
          
          children.push(
            new Paragraph({
              children: questionTextRuns,
              spacing: { before: 100, after: 50 },
            })
          );
          
          // Render based on question type
          switch(typeIdNum) {
            case 1: // Multiple Choice - Smart layout with proper spacing
              // Question text already added above
              if (content.options && content.options.length > 0) {
                const layout = calculateAnswerLayout(content.options);
                const options = content.options;
                
                if (layout.columns === 4 && layout.rows === 1) {
                  // All on 1 line - distribute evenly
                  const spaceBetween = '          '; // Multiple spaces for even distribution
                  const optionTexts = options.map((opt: string, i: number) => {
                    const label = String.fromCharCode(65 + i);
                    const isCorrect = viewMode === 'teacher' && content.correct_index === i;
                    return new TextRun({
                      text: `${label}. ${opt}${isCorrect ? ' ✓' : ''}`,
                      bold: isCorrect,
                      size: 22,
                      color: isCorrect ? '00FF00' : '000000',
                    });
                  });
                  
                  // Insert spaces between options
                  const finalTexts: any[] = [];
                  optionTexts.forEach((opt: any, idx: number) => {
                    finalTexts.push(opt);
                    if (idx < optionTexts.length - 1) {
                      finalTexts.push(new TextRun({ text: spaceBetween, size: 22 }));
                    }
                  });
                  
                  children.push(
                    new Paragraph({
                      children: finalTexts,
                      spacing: { after: 50 },
                    })
                  );
                } else if (layout.columns === 2 && layout.rows === 2) {
                  // 2 columns, 2 rows
                  for (let i = 0; i < options.length; i += 2) {
                    const optionParts: any[] = [];
                    
                    // First column
                    let label = String.fromCharCode(65 + i);
                    let isCorrect = viewMode === 'teacher' && content.correct_index === i;
                    optionParts.push(
                      new TextRun({
                        text: `${label}. ${options[i]}${isCorrect ? ' ✓' : ''}`,
                        bold: isCorrect,
                        size: 22,
                        color: isCorrect ? '00FF00' : '000000',
                      })
                    );
                    optionParts.push(new TextRun({ text: '          ', size: 22 }));
                    
                    // Second column
                    if (i + 1 < options.length) {
                      label = String.fromCharCode(65 + i + 1);
                      isCorrect = viewMode === 'teacher' && content.correct_index === i + 1;
                      optionParts.push(
                        new TextRun({
                          text: `${label}. ${options[i + 1]}${isCorrect ? ' ✓' : ''}`,
                          bold: isCorrect,
                          size: 22,
                          color: isCorrect ? '00FF00' : '000000',
                        })
                      );
                    }
                    
                    children.push(
                      new Paragraph({
                        children: optionParts,
                        spacing: { after: 50 },
                      })
                    );
                  }
                } else {
                  // Vertical layout
                  options.forEach((opt: string, i: number) => {
                    const label = String.fromCharCode(65 + i);
                    const isCorrect = viewMode === 'teacher' && content.correct_index === i;
                    children.push(
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${label}. `, bold: true, size: 22 }),
                          new TextRun({ text: opt, bold: isCorrect, size: 22, color: isCorrect ? '00FF00' : '000000' }),
                          ...(isCorrect ? [new TextRun({ text: ' ✓', bold: true, size: 22, color: '00FF00' })] : []),
                        ],
                        spacing: { after: 50 },
                        indent: { left: 360 },
                      })
                    );
                  });
                }
              }
              break;
              
            case 2: // Verb Conjugation
              // Question text already added above
              // Just show answer key in teacher mode, no dots needed
              if (viewMode === 'teacher' && content.correct_answer) {
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'Đáp án: ', bold: true }),
                      new TextRun({ text: content.correct_answer, color: '00FF00' }),
                    ],
                    spacing: { before: 50, after: 100 },
                  })
                );
              }
              break;
              
            case 3: // Sentence Ordering
              // Question text (words) already added above on same line
              // Arrow and dots already added to same line above if wordCount < 11
              // If wordCount >= 11, add arrow and dots on next line
              const wordCount3 = content.words ? content.words.length : 0;
              
              if (wordCount3 >= 11) {
                // Long: arrow and dots on next line
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: '→ ' }),
                      new TextRun({ text: '.................................................................' }),
                    ],
                    spacing: { after: 100 },
                  })
                );
              }
              if (viewMode === 'teacher') {
                if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Đáp án: ', bold: true }),
                        new TextRun({ text: content.correct_answers.join(' | '), color: '00FF00' }),
                      ],
                      spacing: { before: 50, after: 100 },
                    })
                  );
                } else if (content.correct_sentence) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Đáp án: ', bold: true }),
                        new TextRun({ text: content.correct_sentence, color: '00FF00' }),
                      ],
                      spacing: { before: 50, after: 100 },
                    })
                  );
                }
              }
              break;
              
            case 4: // Write Sentence
              // First prompt already added on same line with question number
              // Add remaining prompts
              if (content.prompts) {
                content.prompts.forEach((prompt: any, i: number) => {
                  // Skip first prompt as it's already on the first line
                  if (i === 0) return;
                  
                  children.push(
                    new Paragraph({
                      text: prompt.text,
                      indent: { left: 360 },
                      spacing: { after: 50 },
                    })
                  );
                });
                // Show answers in teacher mode
                if (viewMode === 'teacher') {
                  content.prompts.forEach((prompt: any) => {
                    if (prompt.answers && Array.isArray(prompt.answers) && prompt.answers.length > 0) {
                      const answers = prompt.answers.filter(Boolean).join(' / ');
                      children.push(
                        new Paragraph({
                          children: [
                            new TextRun({ text: 'Đáp án: ', bold: true }),
                            new TextRun({ text: answers, color: '00FF00' }),
                          ],
                          indent: { left: 360 },
                          spacing: { after: 50 },
                        })
                      );
                    } else if (prompt.answer) {
                      children.push(
                        new Paragraph({
                          children: [
                            new TextRun({ text: 'Đáp án: ', bold: true }),
                            new TextRun({ text: prompt.answer, color: '00FF00' }),
                          ],
                          indent: { left: 360 },
                          spacing: { after: 50 },
                        })
                      );
                    }
                  });
                }
              }
              // Type 4 arrow and dots already added to same line above if wordCount < 11
              // If wordCount >= 11, add arrow and dots on next line
              const prompt1Text = content.prompts && content.prompts.length > 0 ? content.prompts[0].text : '';
              const wordCount4 = prompt1Text.includes('/')
                ? prompt1Text.split('/').filter((w: string) => w.trim().length > 0).length
                : prompt1Text.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
              
              if (wordCount4 >= 11) {
                // Long: arrow and dots on next line
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: '→ ' }),
                      new TextRun({ text: '.................................................................' }),
                    ],
                    spacing: { before: 50, after: 100 },
                  })
                );
              }
              break;

            case 11: // Make Question
              // Sentence with underline and ID already added to header line above
              // Arrow and dots already added to same line above if wordCount < 11
              // If wordCount >= 11, add arrow and dots on next line
              const sentence11 = content.sentence || '';
              const wordCount11 = sentence11.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
              
              if (wordCount11 >= 11) {
                // Long: arrow and dots on next line
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: '→ ' }),
                      new TextRun({ text: '.................................................................' }),
                    ],
                    spacing: { after: 100 },
                  })
                );
              }
              
              if (viewMode === 'teacher') {
                if (content.correct_questions && Array.isArray(content.correct_questions) && content.correct_questions.length > 0) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Đáp án: ', bold: true }),
                        new TextRun({ text: content.correct_questions.join(' | '), color: '00FF00' }),
                      ],
                      spacing: { before: 50, after: 100 },
                    })
                  );
                } else if (content.correct_question) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Đáp án: ', bold: true }),
                        new TextRun({ text: content.correct_question, color: '00FF00' }),
                      ],
                      spacing: { before: 50, after: 100 },
                    })
                  );
                }
              }
              break;

            case 5: // Rewrite Sentence
              // Question text (original_sentence) already added above
              if (content.instruction) {
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: `→ ${content.instruction} ` }),
                      new TextRun({ text: '....................................................................' }),
                    ],
                    spacing: { after: 100 },
                  })
                );
              }
              if (viewMode === 'teacher') {
                if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Đáp án: ', bold: true }),
                        new TextRun({ text: content.correct_answers.join(' | '), color: '00FF00' }),
                      ],
                      spacing: { before: 50, after: 100 },
                    })
                  );
                } else if (content.correct_answer) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Đáp án: ', bold: true }),
                        new TextRun({ text: content.correct_answer, color: '00FF00' }),
                      ],
                      spacing: { before: 50, after: 100 },
                    })
                  );
                }
              }
              break;
              
            case 6: // Reading Essay
            case 7: // Reading MCQ
              // Passage already added to header line above
              if (content.questions) {
                content.questions.forEach((subQ: any, i: number) => {
                  const questionText = `${String.fromCharCode(97 + i)}) ${subQ.question_text}`;
                  
                  // Type 7: No dots, just question text
                  if (typeIdNum === 7) {
                    children.push(
                      new Paragraph({
                        text: questionText,
                        indent: { left: 360 },
                        spacing: { after: 50 },
                      })
                    );
                  } else {
                    // Type 6: Keep dots logic
                    const wordCount = questionText.split(/\s+/).length;
                    
                    if (wordCount < 11) {
                      // Short question: dots on same line
                      children.push(
                        new Paragraph({
                          children: [
                            new TextRun({ text: questionText }),
                            new TextRun({ text: ' ................................................................' }),
                          ],
                          indent: { left: 360 },
                          spacing: { after: 50 },
                        })
                      );
                    } else {
                      // Long question: dots on next line
                      children.push(
                        new Paragraph({
                          text: questionText,
                          indent: { left: 360 },
                          spacing: { after: 50 },
                        })
                      );
                      children.push(
                        new Paragraph({
                          text: '....................................................................',
                          indent: { left: 360 },
                          spacing: { after: 50 },
                        })
                      );
                    }
                    
                    // Add correct answers for Type 6 in teacher mode
                    if (typeIdNum === 6 && viewMode === 'teacher' && subQ.correct_answers && subQ.correct_answers.length > 0) {
                      const answers = subQ.correct_answers.filter((a: string) => a.trim()).join(' / ');
                      children.push(
                        new Paragraph({
                          children: [
                            new TextRun({ text: 'Đáp án: ', bold: true }),
                            new TextRun({ text: answers, color: '00FF00' }),
                          ],
                          indent: { left: 360 },
                          spacing: { before: 50, after: 100 },
                        })
                      );
                    }
                  }
                  
                  if (typeIdNum === 7 && subQ.options) {
                    // Smart layout for Type 7 options (same as Type 1)
                    const layout = calculateAnswerLayout(subQ.options);
                    const options = subQ.options;
                    
                    if (layout.columns === 4 && layout.rows === 1) {
                      // All on 1 line - distribute evenly
                      const spaceBetween = '          ';
                      const optionTexts = options.map((opt: string, j: number) => {
                        const label = String.fromCharCode(65 + j);
                        const isCorrect = viewMode === 'teacher' && subQ.correct_index === j;
                        return new TextRun({
                          text: `${label}. ${opt}${isCorrect ? ' ✓' : ''}`,
                          bold: isCorrect,
                          size: 22,
                          color: isCorrect ? '00FF00' : '000000',
                        });
                      });
                      
                      // Insert spaces between options
                      const finalTexts: any[] = [];
                      optionTexts.forEach((opt: any, idx: number) => {
                        finalTexts.push(opt);
                        if (idx < optionTexts.length - 1) {
                          finalTexts.push(new TextRun({ text: spaceBetween, size: 22 }));
                        }
                      });
                      
                      children.push(
                        new Paragraph({
                          children: finalTexts,
                          indent: { left: 360 },
                          spacing: { after: 50 },
                        })
                      );
                    } else if (layout.columns === 2 && layout.rows === 2) {
                      // 2 columns, 2 rows
                      for (let j = 0; j < options.length; j += 2) {
                        const optionParts: any[] = [];
                        
                        // First column
                        let label = String.fromCharCode(65 + j);
                        let isCorrect = viewMode === 'teacher' && subQ.correct_index === j;
                        optionParts.push(
                          new TextRun({
                            text: `${label}. ${options[j]}${isCorrect ? ' ✓' : ''}`,
                            bold: isCorrect,
                            color: isCorrect ? '00FF00' : '000000',
                          })
                        );
                        optionParts.push(new TextRun({ text: '          ' }));
                        
                        // Second column
                        if (j + 1 < options.length) {
                          label = String.fromCharCode(65 + j + 1);
                          isCorrect = viewMode === 'teacher' && subQ.correct_index === j + 1;
                          optionParts.push(
                            new TextRun({
                              text: `${label}. ${options[j + 1]}${isCorrect ? ' ✓' : ''}`,
                              bold: isCorrect,
                              size: 22,
                              color: isCorrect ? '00FF00' : '000000',
                            })
                          );
                        }
                        
                        children.push(
                          new Paragraph({
                            children: optionParts,
                            indent: { left: 360 },
                            spacing: { after: 50 },
                          })
                        );
                      }
                    } else {
                      // Vertical layout
                      options.forEach((opt: string, j: number) => {
                        const label = String.fromCharCode(65 + j);
                        const isCorrect = viewMode === 'teacher' && subQ.correct_index === j;
                        children.push(
                          new Paragraph({
                            children: [
                              new TextRun({ text: `${label}. `, size: 22 }),
                              new TextRun({ text: opt, bold: isCorrect, size: 22, color: isCorrect ? '00FF00' : '000000' }),
                              ...(isCorrect ? [new TextRun({ text: ' ✓', bold: true, size: 22, color: '00FF00' })] : []),
                            ],
                            spacing: { after: 50 },
                            indent: { left: 360 },
                          })
                        );
                      });
                    }
                  }
                });
              }
              break;

            case 8: // Fill Blanks Word Bank
              // Word bank already added to header line above
              // Add text with indexed blanks on next line
              if (content.text) {
                const blankRegex = /_{3,}|\.{3,}/g;
                let blankIndex = 1;
                const textWithIndexes = content.text.replace(blankRegex, () => {
                  const replacement = `(${blankIndex}) ___________`;
                  blankIndex++;
                  return replacement;
                });
                
                children.push(
                  new Paragraph({
                    text: textWithIndexes,
                    spacing: { after: 100 },
                  })
                );
              }
              
              // Question text already added above
              if (viewMode === 'teacher' && content.answers) {
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'Đáp án: ', bold: true }),
                      new TextRun({ text: content.answers.map((ans: string, i: number) => `${i + 1}) ${ans}`).join(', '), color: '00FF00' }),
                    ],
                    spacing: { before: 50, after: 100 },
                  })
                );
              }
              break;

            case 9: { // Fill Blanks MCQ
              // Passage with indexed blanks
              if (content.passage) {
                const blankRegex = /_{3,}|\.{3,}/g;
                let blankIndex = 1;
                const textWithIndexes = content.passage.replace(blankRegex, () => `(${blankIndex++}) ____`);
                children.push(
                  new Paragraph({
                    text: textWithIndexes,
                    spacing: { after: 80 },
                  })
                );
              }

              if (Array.isArray(content.questions)) {
                content.questions.forEach((subQ: any, i: number) => {
                  children.push(
                    new Paragraph({
                      children: [new TextRun({ text: `Blank ${i + 1}:`, bold: true })],
                      indent: { left: 360 },
                      spacing: { after: 30 },
                    })
                  );

                  if (subQ.options) {
                    const layout = calculateAnswerLayout(subQ.options);

                    if (layout.columns === 4) {
                      const optionTexts = subQ.options.map((opt: string, j: number) => {
                        const label = String.fromCharCode(65 + j);
                        const isCorrect = viewMode === 'teacher' && subQ.correct_index === j;
                        return new TextRun({
                          text: `${label}. ${opt}${isCorrect ? ' ✓' : ''}`,
                          bold: isCorrect,
                          size: 22,
                          color: isCorrect ? '00FF00' : '000000',
                        });
                      });
                      const finalTexts: any[] = [];
                      optionTexts.forEach((opt: any, idx: number) => {
                        finalTexts.push(opt);
                        if (idx < optionTexts.length - 1) finalTexts.push(new TextRun({ text: '      ', size: 22 }));
                      });
                      children.push(
                        new Paragraph({
                          children: finalTexts,
                          indent: { left: 480 },
                          spacing: { after: 40 },
                        })
                      );
                    } else if (layout.columns === 2) {
                      for (let j = 0; j < subQ.options.length; j += 2) {
                        const rowParts: any[] = [];
                        const label1 = String.fromCharCode(65 + j);
                        const isCorrect1 = viewMode === 'teacher' && subQ.correct_index === j;
                        rowParts.push(new TextRun({
                          text: `${label1}. ${subQ.options[j]}${isCorrect1 ? ' ✓' : ''}`,
                          bold: isCorrect1,
                          size: 22,
                          color: isCorrect1 ? '00FF00' : '000000',
                        }));
                        rowParts.push(new TextRun({ text: '          ', size: 22 }));
                        if (j + 1 < subQ.options.length) {
                          const label2 = String.fromCharCode(65 + j + 1);
                          const isCorrect2 = viewMode === 'teacher' && subQ.correct_index === j + 1;
                          rowParts.push(new TextRun({
                            text: `${label2}. ${subQ.options[j + 1]}${isCorrect2 ? ' ✓' : ''}`,
                            bold: isCorrect2,
                            size: 22,
                            color: isCorrect2 ? '00FF00' : '000000',
                          }));
                        }
                        children.push(
                          new Paragraph({
                            children: rowParts,
                            indent: { left: 480 },
                            spacing: { after: 40 },
                          })
                        );
                      }
                    } else {
                      subQ.options.forEach((opt: string, j: number) => {
                        const label = String.fromCharCode(65 + j);
                        const isCorrect = viewMode === 'teacher' && subQ.correct_index === j;
                        children.push(
                          new Paragraph({
                            children: [
                              new TextRun({ text: `${label}. `, size: 22 }),
                              new TextRun({ text: opt, bold: isCorrect, size: 22, color: isCorrect ? '00FF00' : '000000' }),
                              ...(isCorrect ? [new TextRun({ text: ' ✓', bold: true, size: 22, color: '00FF00' })] : []),
                            ],
                            indent: { left: 480 },
                            spacing: { after: 40 },
                          })
                        );
                      });
                    }
                  }
                });
              }
              break;
            }

            case 10: { // Error Identification - same as Type 1 MCQ with options
              // Question text (sentence) already added to header line above
              // No need to add sentence again here
              
              // Add options (same layout logic as Type 1)
              if (content.options && content.options.length > 0) {
                const layout = calculateAnswerLayout(content.options);
                const options = content.options;
                
                if (layout.columns === 4 && layout.rows === 1) {
                  // All on 1 line - distribute evenly
                  const spaceBetween = '          '; // Multiple spaces for even distribution
                  const optionTexts = options.map((opt: string, i: number) => {
                    const label = String.fromCharCode(65 + i);
                    const isWrongOption = viewMode === 'teacher' && content.wrong_index === i;
                    return new TextRun({
                      text: `${label}. ${opt}${isWrongOption ? ' (Error)' : ''}`,
                      bold: isWrongOption,
                      color: isWrongOption ? 'FF0000' : '000000',
                    });
                  });
                  
                  // Insert spaces between options
                  const finalTexts: any[] = [];
                  optionTexts.forEach((opt: any, idx: number) => {
                    finalTexts.push(opt);
                    if (idx < optionTexts.length - 1) {
                      finalTexts.push(new TextRun({ text: spaceBetween }));
                    }
                  });
                  
                  children.push(
                    new Paragraph({
                      children: finalTexts,
                      spacing: { after: 50 },
                    })
                  );
                } else if (layout.columns === 2 && layout.rows === 2) {
                  // 2 columns, 2 rows
                  for (let i = 0; i < options.length; i += 2) {
                    const optionParts: any[] = [];
                    
                    // First column
                    let label = String.fromCharCode(65 + i);
                    let isWrongOption = viewMode === 'teacher' && content.wrong_index === i;
                    optionParts.push(
                      new TextRun({
                        text: `${label}. ${options[i]}${isWrongOption ? ' (Error)' : ''}`,
                        bold: isWrongOption,
                        size: 22,
                        color: isWrongOption ? 'FF0000' : '000000',
                      })
                    );
                    optionParts.push(new TextRun({ text: '          ', size: 22 }));
                    
                    // Second column
                    if (i + 1 < options.length) {
                      label = String.fromCharCode(65 + i + 1);
                      isWrongOption = viewMode === 'teacher' && content.wrong_index === i + 1;
                      optionParts.push(
                        new TextRun({
                          text: `${label}. ${options[i + 1]}${isWrongOption ? ' (Error)' : ''}`,
                          bold: isWrongOption,
                          size: 22,
                          color: isWrongOption ? 'FF0000' : '000000',
                        })
                      );
                    }
                    
                    children.push(
                      new Paragraph({
                        children: optionParts,
                        spacing: { after: 50 },
                      })
                    );
                  }
                } else {
                  // Vertical layout
                  options.forEach((opt: string, i: number) => {
                    const label = String.fromCharCode(65 + i);
                    const isWrongOption = viewMode === 'teacher' && content.wrong_index === i;
                    children.push(
                      new Paragraph({
                        children: [
                          new TextRun({ text: `${label}. `, bold: true, size: 22 }),
                          new TextRun({ text: opt, bold: isWrongOption, size: 22, color: isWrongOption ? 'FF0000' : '000000' }),
                          ...(isWrongOption ? [new TextRun({ text: ' (Error)', bold: true, size: 22, color: 'FF0000' })] : []),
                        ],
                        spacing: { after: 50 },
                        indent: { left: 360 },
                      })
                    );
                  });
                }
              }
              
              if (viewMode === 'teacher') {
                if (content.error_word) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Lỗi sai: ', bold: true }),
                        new TextRun({ text: content.error_word, color: 'FF0000' }),
                      ],
                      spacing: { before: 50 },
                    })
                  );
                }
                if (content.correction) {
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: 'Sửa thành: ', bold: true }),
                        new TextRun({ text: content.correction, color: '00FF00' }),
                      ],
                      spacing: { after: 100 },
                    })
                  );
                }
              }
              break;
            }
              
            case 12: // True/False
              {
                const statements = Array.isArray(content.statements) && content.statements.length
                  ? content.statements
                  : (content.text ? [{ text: content.text, is_true: content.is_true }] : []);
                statements.forEach((s: any, idx: number) => {
                  const questionIdRun = q.id ? new TextRun({ text: ` [${q.id}]`, italics: true, color: '666666' }) : null;
                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: `${idx + 1}) ${s.text || ''}` }),
                        ...(questionIdRun ? [questionIdRun] : []),
                        new TextRun({ text: '   ' }),
                        new TextRun({ text: '☐ True     ☐ False' }),
                      ],
                      spacing: { after: 50 },
                    })
                  );
                  if (viewMode === 'teacher') {
                    children.push(
                      new Paragraph({
                        children: [
                          new TextRun({ text: 'Đáp án: ', bold: true }),
                          new TextRun({ text: s.is_true ? 'True' : 'False', color: '00FF00' }),
                        ],
                        spacing: { before: 20, after: 80 },
                        indent: { left: 360 },
                      })
                    );
                  }
                });
              }
              break;
              
            case 13: // Part of Speech - Điền loại từ (similar to type 2)
              // Question text already added above
              // Just show answer key in teacher mode
              if (viewMode === 'teacher' && content.correct_answer) {
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: 'Đáp án: ', bold: true }),
                      new TextRun({ text: content.correct_answer, color: '00FF00' }),
                    ],
                    spacing: { before: 50, after: 100 },
                  })
                );
              }
              break;
              
            default:
              const displayText = content.text || content.passage || JSON.stringify(content);
              children.push(new Paragraph({ text: displayText, spacing: { after: 100 } }));
          }
          
          // Add score (teacher mode)
          if (viewMode === 'teacher') {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `[${eq.score || 1} điểm]`, italics: true, color: '666666' }),
                ],
                spacing: { before: 50, after: 150 },
              })
            );
          }
          
          globalQuestionNum++; // Increment global counter
        });
        
        exerciseNum++;
      });
      
      // Create document with minimal margins (1cm = 567 twips)
      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: {
                top: 567,    // 1cm
                bottom: 567, // 1cm
                left: 567,   // 1cm
                right: 567,  // 1cm
              },
            },
          },
          children: children,
        }],
      });
      
      // Generate and download with format: [exam_id]_exam_title_datetime.docx
      const blob = await Packer.toBlob(doc);
      const now = new Date();
      const dateTime = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      // Use exam title directly to support Vietnamese characters
      const fileName = `[${exam.id}]_${exam.title}_${dateTime}.docx`;
      saveAs(blob, fileName);
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Có lỗi xảy ra khi xuất file. Vui lòng thử lại!');
    }
  };

  // Render question in print mode (Word-like format)
  const renderPrintQuestion = (eq: any, displayNumber: number) => {
    const q = eq.question;
    const typeIdNum = q.question_type_id;
    let content = q.content;
    
    if (typeof content === 'string') {
      try {
        content = JSON.parse(content);
      } catch (e) {
        content = { text: content };
      }
    }

    return (
      <div key={q.id} style={{ marginBottom: '16px', pageBreakInside: 'avoid' }}>
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontWeight: 'bold' }}>{displayNumber}. </span>
          {/* Question text based on type */}
          {typeIdNum === 1 && <span>{content.text || ''} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></span>}
          {(typeIdNum === 2 || typeIdNum === 13) && <span>{content.text || ''} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></span>}
          {typeIdNum === 3 && (
            <>
              <span>{(content.words || []).join(' / ')} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></span>
              {content.words && content.words.length < 11 ? (
                <span> → .................................................................</span>
              ) : (
                <div>→ .................................................................</div>
              )}
            </>
          )}
          {typeIdNum === 4 && content.prompts && content.prompts.length > 0 && (
            <>
              <span>{content.prompts[0].text} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></span>
              {(() => {
                const firstPrompt = content.prompts[0].text;
                const wordCount = firstPrompt.includes('/') 
                  ? firstPrompt.split('/').filter((w: string) => w.trim().length > 0).length
                  : firstPrompt.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
                
                if (wordCount < 11) {
                  return <span> → .................................................................</span>;
                } else {
                  return <div>→ .................................................................</div>;
                }
              })()}
            </>
          )}
          {typeIdNum === 5 && (
            <>
              <span>{content.original_sentence || ''} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></span>
              {content.instruction && <div>→ {content.instruction} ....................................................................</div>}
            </>
          )}
          {(typeIdNum === 6 || typeIdNum === 7) && (
            <span>{content.passage || ''} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></span>
          )}
          {typeIdNum === 8 && (
            <>
              {content.word_bank && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>📦 Word Bank:</strong> {content.word_bank.join(' / ')}
                </div>
              )}
              <span>{content.text || ''} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></span>
            </>
          )}
          {typeIdNum === 10 && (
            <div style={{ marginBottom: '12px' }}>{content.sentence || ''} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></div>
          )}
          {typeIdNum === 11 && (
            <>
              {(() => {
                const sentence = content.sentence || '';
                const underlined = content.underlined || '';
                if (sentence && underlined && sentence.includes(underlined)) {
                  const parts = sentence.split(underlined);
                  return (
                    <span>
                      {parts[0]}<u style={{ fontWeight: 'bold' }}>{underlined}</u>{parts[1] || ''} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span>
                    </span>
                  );
                }
                return <span>{sentence} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></span>;
              })()}
              {(() => {
                const sentence = content.sentence || '';
                const wordCount = sentence.trim().split(/\s+/).filter((w: string) => w.length > 0).length;
                if (wordCount < 11) {
                  return <span> → .................................................................</span>;
                } else {
                  return <div>→ .................................................................</div>;
                }
              })()}
            </>
          )}
          {typeIdNum === 12 && (() => {
            const statements = Array.isArray(content.statements) && content.statements.length
              ? content.statements
              : (content.text ? [{ text: content.text, is_true: content.is_true }] : []);
            return (
              <>
                {content.passage && (
                  <span>{content.passage} <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}>[{q.id}]</span></span>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: '6px' }}>
                  {statements.map((s: any, idx: number) => (
                    <div key={idx} style={{ padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
                      <div style={{ fontWeight: 600 }}>
                        Câu {idx + 1}: {s.text}
                        {!content.passage && idx === 0 && (
                          <span style={{ fontStyle: 'italic', fontSize: '11px', color: '#666' }}> [{q.id}]</span>
                        )}
                        {'   '}☐ True&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;☐ False
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>

        {/* Options for Multiple Choice (Type 1) and Error Identification (Type 10) */}
        {(typeIdNum === 1 || typeIdNum === 10) && content.options && (
          <div style={{ marginLeft: '20px' }}>
            {(() => {
              const layout = calculateAnswerLayout(content.options);
              if (layout.columns === 4) {
                return (
                  <div>
                    {content.options.map((opt: string, i: number) => {
                      const label = String.fromCharCode(65 + i);
                      const isCorrect = typeIdNum === 1 ? (viewMode === 'teacher' && content.correct_index === i) : (viewMode === 'teacher' && content.wrong_index === i);
                      const textColor = typeIdNum === 1 ? '#00FF00' : '#FF0000';
                      const mark = typeIdNum === 1 ? ' ✓' : ' (Error)';
                      return (
                        <span key={i} style={{ marginRight: '24px', fontWeight: isCorrect ? 'bold' : 'normal', color: isCorrect ? textColor : '#000' }}>
                          {label}. {opt}{isCorrect ? mark : ''}
                        </span>
                      );
                    })}
                  </div>
                );
              } else if (layout.columns === 2) {
                const rows = [];
                for (let i = 0; i < content.options.length; i += 2) {
                  rows.push(
                    <div key={i}>
                      {(() => {
                        const label1 = String.fromCharCode(65 + i);
                        const isCorrect1 = typeIdNum === 1 ? (viewMode === 'teacher' && content.correct_index === i) : (viewMode === 'teacher' && content.wrong_index === i);
                        const textColor = typeIdNum === 1 ? '#00FF00' : '#FF0000';
                        const mark = typeIdNum === 1 ? ' ✓' : ' (Error)';
                        return (
                          <span style={{ marginRight: '24px', fontWeight: isCorrect1 ? 'bold' : 'normal', color: isCorrect1 ? textColor : '#000' }}>
                            {label1}. {content.options[i]}{isCorrect1 ? mark : ''}
                          </span>
                        );
                      })()}
                      {i + 1 < content.options.length && (() => {
                        const label2 = String.fromCharCode(65 + i + 1);
                        const isCorrect2 = typeIdNum === 1 ? (viewMode === 'teacher' && content.correct_index === i + 1) : (viewMode === 'teacher' && content.wrong_index === i + 1);
                        const textColor = typeIdNum === 1 ? '#00FF00' : '#FF0000';
                        const mark = typeIdNum === 1 ? ' ✓' : ' (Error)';
                        return (
                          <span style={{ fontWeight: isCorrect2 ? 'bold' : 'normal', color: isCorrect2 ? textColor : '#000' }}>
                            {label2}. {content.options[i + 1]}{isCorrect2 ? mark : ''}
                          </span>
                        );
                      })()}
                    </div>
                  );
                }
                return <>{rows}</>;
              } else {
                return content.options.map((opt: string, i: number) => {
                  const label = String.fromCharCode(65 + i);
                  const isCorrect = typeIdNum === 1 ? (viewMode === 'teacher' && content.correct_index === i) : (viewMode === 'teacher' && content.wrong_index === i);
                  const textColor = typeIdNum === 1 ? '#00FF00' : '#FF0000';
                  const mark = typeIdNum === 1 ? ' ✓' : ' (Error)';
                  return (
                    <div key={i} style={{ fontWeight: isCorrect ? 'bold' : 'normal', color: isCorrect ? textColor : '#000' }}>
                      {label}. {opt}{isCorrect ? mark : ''}
                    </div>
                  );
                });
              }
            })()}
          </div>
        )}

        {/* Options for Error Identification (Type 10) - Same as Type 1 MCQ */}
        {typeIdNum === 10 && content.options && (
          <div style={{ marginLeft: '20px' }}>
            {(() => {
              const layout = calculateAnswerLayout(content.options);
              if (layout.columns === 4) {
                return (
                  <div>
                    {content.options.map((opt: string, i: number) => {
                      const label = String.fromCharCode(65 + i);
                      const isWrongOption = viewMode === 'teacher' && content.wrong_index === i;
                      return (
                        <span key={i} style={{ marginRight: '24px', fontWeight: isWrongOption ? 'bold' : 'normal', color: isWrongOption ? '#FF0000' : '#000' }}>
                          {label}. {opt}{isWrongOption ? ' (Error)' : ''}
                        </span>
                      );
                    })}
                  </div>
                );
              } else if (layout.columns === 2) {
                const rows = [];
                for (let i = 0; i < content.options.length; i += 2) {
                  rows.push(
                    <div key={i}>
                      {(() => {
                        const label1 = String.fromCharCode(65 + i);
                        const isWrongOption1 = viewMode === 'teacher' && content.wrong_index === i;
                        return (
                          <span style={{ marginRight: '24px', fontWeight: isWrongOption1 ? 'bold' : 'normal', color: isWrongOption1 ? '#FF0000' : '#000' }}>
                            {label1}. {content.options[i]}{isWrongOption1 ? ' (Error)' : ''}
                          </span>
                        );
                      })()}
                      {i + 1 < content.options.length && (() => {
                        const label2 = String.fromCharCode(65 + i + 1);
                        const isWrongOption2 = viewMode === 'teacher' && content.wrong_index === i + 1;
                        return (
                          <span style={{ fontWeight: isWrongOption2 ? 'bold' : 'normal', color: isWrongOption2 ? '#FF0000' : '#000' }}>
                            {label2}. {content.options[i + 1]}{isWrongOption2 ? ' (Error)' : ''}
                          </span>
                        );
                      })()}
                    </div>
                  );
                }
                return <>{rows}</>;
              } else {
                return content.options.map((opt: string, i: number) => {
                  const label = String.fromCharCode(65 + i);
                  const isWrongOption = viewMode === 'teacher' && content.wrong_index === i;
                  return (
                    <div key={i} style={{ fontWeight: isWrongOption ? 'bold' : 'normal', color: isWrongOption ? '#FF0000' : '#000' }}>
                      {label}. {opt}{isWrongOption ? ' (Error)' : ''}
                    </div>
                  );
                });
              }
            })()}
          </div>
        )}

        {/* Sub-questions for Reading (Type 6/7) */}
        {(typeIdNum === 6 || typeIdNum === 7) && content.questions && (
          <div style={{ marginLeft: '20px' }}>
            {content.questions.map((subQ: any, i: number) => {
              const questionText = `${String.fromCharCode(97 + i)}) ${subQ.question_text}`;
              return (
                <div key={i} style={{ marginBottom: '8px' }}>
                  {typeIdNum === 6 ? (
                    <>
                      {questionText.split(/\s+/).length < 11 ? (
                        <span>{questionText} ................................................................</span>
                      ) : (
                        <>
                          <div>{questionText}</div>
                          <div>....................................................................</div>
                        </>
                      )}
                      {viewMode === 'teacher' && subQ.correct_answers && subQ.correct_answers.length > 0 && (
                        <div style={{ marginTop: '4px', color: '#28a745', fontSize: '14px' }}>
                          <strong>Đáp án:</strong> {subQ.correct_answers.filter((a: string) => a.trim()).join(' / ')}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>{questionText}</div>
                      {subQ.options && (
                        <div style={{ marginLeft: '20px' }}>
                          {(() => {
                            const layout = calculateAnswerLayout(subQ.options);
                            if (layout.columns === 4) {
                              return (
                                <div>
                                  {subQ.options.map((opt: string, j: number) => {
                                    const label = String.fromCharCode(65 + j);
                                    const isCorrect = viewMode === 'teacher' && subQ.correct_index === j;
                                    return (
                                      <span key={j} style={{ marginRight: '24px', fontWeight: isCorrect ? 'bold' : 'normal', color: isCorrect ? '#00FF00' : '#000' }}>
                                        {label}. {opt}{isCorrect ? ' ✓' : ''}
                                      </span>
                                    );
                                  })}
                                </div>
                              );
                            } else if (layout.columns === 2) {
                              const rows = [];
                              for (let j = 0; j < subQ.options.length; j += 2) {
                                rows.push(
                                  <div key={j}>
                                    {(() => {
                                      const label1 = String.fromCharCode(65 + j);
                                      const isCorrect1 = viewMode === 'teacher' && subQ.correct_index === j;
                                      return (
                                        <span style={{ marginRight: '24px', fontWeight: isCorrect1 ? 'bold' : 'normal', color: isCorrect1 ? '#00FF00' : '#000' }}>
                                          {label1}. {subQ.options[j]}{isCorrect1 ? ' ✓' : ''}
                                        </span>
                                      );
                                    })()}
                                    {j + 1 < subQ.options.length && (() => {
                                      const label2 = String.fromCharCode(65 + j + 1);
                                      const isCorrect2 = viewMode === 'teacher' && subQ.correct_index === j + 1;
                                      return (
                                        <span style={{ fontWeight: isCorrect2 ? 'bold' : 'normal', color: isCorrect2 ? '#00FF00' : '#000' }}>
                                          {label2}. {subQ.options[j + 1]}{isCorrect2 ? ' ✓' : ''}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                );
                              }
                              return <>{rows}</>;
                            } else {
                              return subQ.options.map((opt: string, j: number) => {
                                const label = String.fromCharCode(65 + j);
                                const isCorrect = viewMode === 'teacher' && subQ.correct_index === j;
                                return (
                                  <div key={j} style={{ fontWeight: isCorrect ? 'bold' : 'normal', color: isCorrect ? '#00FF00' : '#000' }}>
                                    {label}. {opt}{isCorrect ? ' ✓' : ''}
                                  </div>
                                );
                              });
                            }
                          })()}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Additional prompts for Type 4 */}
        {typeIdNum === 4 && content.prompts && content.prompts.slice(1).map((prompt: any, i: number) => (
          <div key={i} style={{ marginLeft: '20px' }}>{prompt.text}</div>
        ))}

        {/* Teacher mode answers */}
        {viewMode === 'teacher' && (
          <div style={{ marginTop: '8px', color: '#00FF00' }}>
            {(typeIdNum === 2 || typeIdNum === 13) && content.correct_answer && (
              <div><strong>Đáp án:</strong> {content.correct_answer}</div>
            )}
            {typeIdNum === 3 && viewMode === 'teacher' && (() => {
              if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                return <div><strong>Đáp án:</strong> {content.correct_answers.join(' | ')}</div>;
              }
              if (content.correct_sentence) {
                return <div><strong>Đáp án:</strong> {content.correct_sentence}</div>;
              }
              return null;
            })()}
            {typeIdNum === 4 && content.prompts && content.prompts.some((p: any) => p.answer) && (
              content.prompts.map((prompt: any, i: number) => 
                prompt.answer && <div key={i}><strong>Đáp án:</strong> {prompt.answer}</div>
              )
            )}
            {typeIdNum === 5 && viewMode === 'teacher' && (() => {
              if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                return <div><strong>Đáp án:</strong> {content.correct_answers.join(' | ')}</div>;
              }
              if (content.correct_answer) {
                return <div><strong>Đáp án:</strong> {content.correct_answer}</div>;
              }
              return null;
            })()}
            {typeIdNum === 8 && content.answers && (
              <div><strong>Đáp án:</strong> {content.answers.join(', ')}</div>
            )}
            {typeIdNum === 10 && (
              <>
                {content.wrong_index !== undefined && content.options && content.options[content.wrong_index] && (
                  <div><strong>Lỗi sai:</strong> <span style={{ color: '#FF0000' }}>{String.fromCharCode(65 + content.wrong_index)}. {content.options[content.wrong_index]}</span></div>
                )}
                {content.error_word && <div><strong>Từ sai:</strong> {content.error_word}</div>}
                {content.correction && <div><strong>Sửa thành:</strong> {content.correction}</div>}
              </>
            )}
            {typeIdNum === 11 && content.correct_question && (
              <div><strong>Đáp án:</strong> {content.correct_question}</div>
            )}
            {typeIdNum === 12 && (() => {
              const statements = Array.isArray(content.statements) && content.statements.length
                ? content.statements
                : (content.text ? [{ text: content.text, is_true: content.is_true }] : []);
              if (!statements.length) return null;
              return (
                <div>
                  <strong>Đáp án:</strong>
                  <div style={{ marginTop: '4px' }}>
                    {statements.map((s: any, idx: number) => (
                      <div key={idx}>Câu {idx + 1}: {s.is_true ? 'True' : 'False'}</div>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div style={{ fontStyle: 'italic', color: '#666', marginTop: '4px' }}>[{eq.score || 1} điểm]</div>
          </div>
        )}
      </div>
    );
  };

  const renderQuestion = (examQuestion: any, displayNumber: number) => {
    const question = examQuestion.question;
    const questionTypeId = question.question_type_id;

    // Parse content if it's a JSON string
    let content = question.content;
    if (typeof content === 'string') {
      try {
        content = JSON.parse(content);
      } catch (e) {
        // If parsing fails, keep it as string
        content = { text: content }; // Wrap in object
      }
    }
    
    // Get question type name from database fields
    const questionTypeName = question.questionType?.vn_title 
      || question.questionType?.type_name 
      || question.questionType?.eng_title;

    // Common header for all questions
    const QuestionHeaderSection = () => (
      <QuestionHeader>
        <QuestionNumber $viewMode={viewMode}>
          {displayNumber}. <QuestionId>[{question.id}]</QuestionId>
        </QuestionNumber>
        {viewMode !== 'print' && (
          <QuestionBadges>
            {question.difficulty && (
              <Badge $type={question.difficulty}>{question.difficulty}</Badge>
            )}
            <Badge $type="score">{examQuestion.score || 1} điểm</Badge>
          </QuestionBadges>
        )}
      </QuestionHeader>
    );

    // Render based on question type with updated formatting
    const renderContent = () => {
      switch(questionTypeId) {
        // 1. Multiple Choice ABCD
        case 1:
          return (
            <>
              <QuestionContent $viewMode={viewMode}>{content.text}</QuestionContent>
              {content.options && content.options.length > 0 && (
                <OptionsList>
                  {content.options.map((option: any, i: number) => {
                    const label = String.fromCharCode(65 + i);
                    const isCorrect = viewMode === 'teacher' && content.correct_index === i;
                    return (
                      <OptionItem key={i} $isCorrect={isCorrect} $viewMode={viewMode}>
                        <OptionLabel>{label}.</OptionLabel>
                        <span>{option}</span>
                      </OptionItem>
                    );
                  })}
                </OptionsList>
              )}
              {viewMode === 'teacher' && content.correct_index !== undefined && (
                <CorrectAnswerSection>
                  <strong>Đáp án đúng:</strong> {String.fromCharCode(65 + content.correct_index)}
                </CorrectAnswerSection>
              )}
            </>
          );

        // 2. Verb Conjugation - add dots
        case 2:
          return (
            <>
              <QuestionContent $viewMode={viewMode}>{content.text}</QuestionContent>
              {viewMode === 'teacher' && content.correct_answer && (
                <CorrectAnswerSection>
                  <strong>Đáp án đúng:</strong> {content.correct_answer}
                  {content.case_sensitive && <div style={{fontSize: '12px', color: '#666'}}>(Phân biệt chữ hoa/thường)</div>}
                </CorrectAnswerSection>
              )}
            </>
          );

        // 3. Sentence Ordering - remove "Sắp xếp các từ", use "/" separator, add dots
        case 3:
          return (
            <>
              {content.text && (
                <QuestionContent $viewMode={viewMode}>{content.text}</QuestionContent>
              )}
              {content.words && (
                <div style={{
                  background: '#f8f9fa',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  fontWeight: 500
                }}>
                  {content.words.join(' / ')}
                </div>
              )}
              <div style={{ 
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                marginTop: '16px'
              }}>
                <span style={{ letterSpacing: '1px' }}>......................................................................</span>
              </div>
              {viewMode === 'teacher' && (() => {
                if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                  return (
                    <CorrectAnswerSection>
                      <strong>Đáp án:</strong> {content.correct_answers.join(' | ')}
                    </CorrectAnswerSection>
                  );
                }
                if (content.correct_sentence) {
                  return (
                    <CorrectAnswerSection>
                      <strong>Đáp án:</strong> {content.correct_sentence}
                    </CorrectAnswerSection>
                  );
                }
                return null;
              })()}
            </>
          );

        // 4. Write Sentence - show prompts without a) b) c) prefixes
        case 4:
          return (
            <>
              {content.prompts && content.prompts.map((prompt: any, i: number) => (
                <div key={i} style={{marginBottom: '12px'}}>
                  <div style={{fontWeight: 600, color: '#2c3e50', marginBottom: '8px'}}>
                    {prompt.text}
                  </div>
                  {viewMode === 'teacher' && (() => {
                    if (prompt.answers && Array.isArray(prompt.answers) && prompt.answers.length > 0) {
                      return (
                        <div style={{color: '#28a745', marginTop: '4px', fontSize: '14px'}}>
                          <strong>Đáp án:</strong> {prompt.answers.filter(Boolean).join(' / ')}
                        </div>
                      );
                    }
                    if (prompt.answer) {
                      return (
                        <div style={{color: '#28a745', marginTop: '4px', fontSize: '14px'}}>
                          <strong>Đáp án:</strong> {prompt.answer}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              ))}
              <div style={{ 
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                marginTop: '16px'
              }}>
                <span style={{ letterSpacing: '1px' }}>......................................................................</span>
              </div>
            </>
          );

        // 5. Rewrite Sentence - show only original_sentence + instruction with arrow, add dots
        case 5:
          return (
            <>
              <QuestionContent $viewMode={viewMode}>
                <div style={{ marginBottom: '8px' }}>
                  {content.original_sentence}
                </div>
                {content.instruction && (
                  <div style={{ marginTop: '8px', color: '#666' }}>
                    → {content.instruction}
                  </div>
                )}
              </QuestionContent>
              <div style={{ 
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                marginTop: '16px'
              }}>
                <span style={{ letterSpacing: '1px' }}>......................................................................</span>
              </div>
              {viewMode === 'teacher' && (() => {
                if (content.correct_answers && Array.isArray(content.correct_answers) && content.correct_answers.length > 0) {
                  return (
                    <CorrectAnswerSection>
                      <strong>Đáp án:</strong> {content.correct_answers.join(' | ')}
                    </CorrectAnswerSection>
                  );
                }
                if (content.correct_answer) {
                  return (
                    <CorrectAnswerSection>
                      <strong>Đáp án:</strong> {content.correct_answer}
                    </CorrectAnswerSection>
                  );
                }
                return null;
              })()}
            </>
          );

        // 6. Reading Essay
        case 6:
          return (
            <>
              <div style={{
                background: '#e7f3ff',
                border: '2px solid #007bff',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                <div style={{fontWeight: 700, color: '#007bff', marginBottom: '8px'}}>📖 Đoạn văn:</div>
                <div style={{whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>{content.passage}</div>
              </div>
              <div><strong>Câu hỏi:</strong></div>
              {content.questions && content.questions.map((q: any, i: number) => (
                <div key={i} style={{marginLeft: '20px', marginTop: '12px'}}>
                  <div style={{fontWeight: 600}}>{String.fromCharCode(97 + i)}) {q.question_text}</div>
                  {viewMode === 'teacher' && q.correct_answers && q.correct_answers.length > 0 && (
                    <div style={{color: '#28a745', marginTop: '4px', fontSize: '14px'}}>
                      <strong>Đáp án:</strong> {q.correct_answers.filter((a: string) => a.trim()).join(' / ')}
                    </div>
                  )}
                </div>
              ))}
            </>
          );

        // 7. Reading MCQ
        case 7:
          return (
            <>
              <div style={{
                background: '#e7f3ff',
                border: '2px solid #007bff',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                <div style={{fontWeight: 700, color: '#007bff', marginBottom: '8px'}}>📖 Đoạn văn:</div>
                <div style={{whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>{content.passage}</div>
              </div>
              {content.questions && content.questions.map((q: any, qIdx: number) => (
                <div key={qIdx} style={{marginBottom: '16px'}}>
                  <div style={{fontWeight: 600, marginBottom: '8px'}}>
                    {String.fromCharCode(97 + qIdx)}) {q.question_text}
                  </div>
                  {q.options && (
                    <OptionsList>
                      {q.options.map((opt: string, i: number) => {
                        const label = String.fromCharCode(65 + i);
                        const isCorrect = viewMode === 'teacher' && q.correct_index === i;
                        return (
                          <OptionItem key={i} $isCorrect={isCorrect} $viewMode={viewMode}>
                            <OptionLabel>{label}.</OptionLabel>
                            <span>{opt}</span>
                          </OptionItem>
                        );
                      })}
                    </OptionsList>
                  )}
                  {viewMode === 'teacher' && q.correct_index !== undefined && (
                    <div style={{color: '#28a745', marginTop: '4px', fontSize: '14px'}}>
                      <strong>Đáp án:</strong> {String.fromCharCode(65 + q.correct_index)}
                    </div>
                  )}
                </div>
              ))}
            </>
          );

        // 8. Fill Blanks Word Bank
        case 8:
          return (
            <>
              <div style={{
                background: '#fff3cd',
                border: '2px solid #ffc107',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px'
              }}>
                <strong>📦 Word Bank:</strong>{' '}
                {content.word_bank && content.word_bank.map((word: string, i: number) => (
                  <span key={i} style={{
                    display: 'inline-block',
                    background: '#ffc107',
                    color: '#000',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    margin: '4px',
                    fontWeight: 600
                  }}>
                    {word}
                  </span>
                ))}
              </div>
              <QuestionContent $viewMode={viewMode}>{content.text}</QuestionContent>
              {viewMode === 'teacher' && content.answers && (
                <CorrectAnswerSection>
                  <strong>Đáp án:</strong> {content.answers.join(', ')}
                </CorrectAnswerSection>
              )}
            </>
          );

        // 9. Fill Blanks MCQ
        case 9:
          return (
            <>
              <QuestionContent $viewMode={viewMode}>
                {(content.passage || '').replace(/_{3,}|\.{3,}/g, (() => {
                  let idx = 1;
                  return () => `(${idx++}) ____`;
                })())}
              </QuestionContent>

              {content.questions && content.questions.map((q: any, qIdx: number) => (
                <div key={qIdx} style={{marginTop: '12px'}}>
                  <div style={{fontWeight: 600, marginBottom: '8px'}}>Chỗ trống {qIdx + 1}:</div>
                  {q.options && (
                    <OptionsList>
                      {q.options.map((opt: string, i: number) => {
                        const label = String.fromCharCode(65 + i);
                        const isCorrect = viewMode === 'teacher' && q.correct_index === i;
                        return (
                          <OptionItem key={i} $isCorrect={isCorrect} $viewMode={viewMode}>
                            <OptionLabel>{label}.</OptionLabel>
                            <span>{opt}</span>
                          </OptionItem>
                        );
                      })}
                    </OptionsList>
                  )}
                  {viewMode === 'teacher' && q.correct_index !== undefined && (
                    <div style={{color: '#28a745', marginTop: '4px', fontSize: '14px'}}>
                      <strong>Đáp án:</strong> {String.fromCharCode(65 + q.correct_index)}
                    </div>
                  )}
                </div>
              ))}
            </>
          );

        // 10. Find Error - Display like Type 1 MCQ
        case 10:
          return (
            <>
                <QuestionContent $viewMode={viewMode}>{content.sentence}</QuestionContent>
              {content.options && content.options.length > 0 && (
                <OptionsList>
                  {content.options.map((option: any, i: number) => {
                    const label = String.fromCharCode(65 + i);
                    const isWrongOption = viewMode === 'teacher' && content.wrong_index === i;
                    return (
                      <OptionItem key={i} $isCorrect={isWrongOption} $viewMode={viewMode} style={{ color: isWrongOption ? '#dc3545' : 'inherit' }}>
                        <OptionLabel>{label}.</OptionLabel>
                        <span>{option}</span>
                      </OptionItem>
                    );
                  })}
                </OptionsList>
              )}
              {viewMode === 'teacher' && content.wrong_index !== undefined && content.options && (
                <CorrectAnswerSection>
                  <div><strong>Lỗi sai:</strong> <span style={{color: '#dc3545', fontWeight: 'bold'}}>{String.fromCharCode(65 + content.wrong_index)}. {content.options[content.wrong_index]}</span></div>
                  {content.error_word && <div><strong>Từ sai:</strong> {content.error_word}</div>}
                  {content.correction && <div><strong>Sửa thành:</strong> {content.correction}</div>}
                  {content.explanation && (
                    <div style={{marginTop: '8px'}}>
                      <strong>Giải thích:</strong> {content.explanation}
                    </div>
                  )}
                </CorrectAnswerSection>
              )}
            </>
          );

        // 11. Make Question
        case 11:
          return (
            <>
              <QuestionContent $viewMode={viewMode}>
                {(() => {
                  const sentence = content.sentence || '';
                  const underlined = content.underlined || content.underlined_part || '';
                  if (sentence && underlined) {
                    const parts = sentence.split(underlined);
                    return (
                      <>
                        {parts[0]}
                        <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
                          {underlined}
                        </span>
                        {parts[1]}
                      </>
                    );
                  }
                  return sentence;
                })()}
              </QuestionContent>
              <div style={{ 
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                marginTop: '16px'
              }}>
                <span style={{ letterSpacing: '1px' }}>......................................................................</span>
              </div>
              {viewMode === 'teacher' && (() => {
                if (content.correct_questions && Array.isArray(content.correct_questions) && content.correct_questions.length > 0) {
                  return (
                    <CorrectAnswerSection>
                      <strong>Đáp án:</strong> {content.correct_questions.join(' | ')}
                    </CorrectAnswerSection>
                  );
                }
                if (content.correct_question || content.sample_question) {
                  return (
                    <CorrectAnswerSection>
                      <strong>Đáp án:</strong> {content.correct_question || content.sample_question}
                    </CorrectAnswerSection>
                  );
                }
                return null;
              })()}
            </>
          );

        // 12. True/False
        case 12:
          return (
            <>
              {content.passage && (
                <QuestionContent $viewMode={viewMode} style={{ fontStyle: 'italic' }}>
                  {content.passage}
                </QuestionContent>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: '8px' }}>
                {(Array.isArray(content.statements) && content.statements.length
                  ? content.statements
                  : (content.text ? [{ text: content.text, is_true: content.is_true }] : [])
                ).map((s: any, idx: number) => (
                  <div key={idx} style={{
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Câu {idx + 1}: {s.text}</div>
                    <span style={{ fontSize: '16px', fontWeight: 600 }}>☐ True     ☐ False</span>
                    {viewMode === 'teacher' && (
                      <CorrectAnswerSection style={{ marginTop: 8 }}>
                        <strong>Đáp án:</strong> {s.is_true ? 'True' : 'False'}
                      </CorrectAnswerSection>
                    )}
                  </div>
                ))}
              </div>
            </>
          );

        // Default: Display as-is
        default:
          const displayText = content.text || content.passage || JSON.stringify(content);
          return <QuestionContent $viewMode={viewMode}>{displayText}</QuestionContent>;
      }
    };

    return (
      <QuestionCard key={examQuestion.id} $viewMode={viewMode}>
        <QuestionHeaderSection />
        {renderContent()}
      </QuestionCard>
    );
  };

  return (
    <ModalOverlay onClick={onClose}>
      <PrintStyles />
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>👁️ Xem Trước Đề Thi</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ViewModeSelector>
          <ViewModeButton 
            $active={viewMode === 'student'} 
            onClick={() => setViewMode('student')}
          >
            👨‍🎓 Chế độ Học Sinh
          </ViewModeButton>
          <ViewModeButton 
            $active={viewMode === 'teacher'} 
            onClick={() => setViewMode('teacher')}
          >
            👩‍🏫 Chế độ Giáo Viên
          </ViewModeButton>
          <ViewModeButton 
            $active={viewMode === 'print'} 
            onClick={() => setViewMode('print')}
          >
            🖨️ Chế độ In Ấn
          </ViewModeButton>
          
          <ActionButtons>
            <ActionButton onClick={handlePrint}>
              📄 In Đề Thi
            </ActionButton>
            <ActionButton onClick={handleExport}>
              💾 Xuất File
            </ActionButton>
          </ActionButtons>
        </ViewModeSelector>

        <ModalBody id="print-content" ref={printContentRef}>
          {loading ? (
            <LoadingContainer>
              <div>Đang tải dữ liệu đề thi...</div>
            </LoadingContainer>
          ) : !exam ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#657786' }}>
              ❌ Không tìm thấy đề thi
            </div>
          ) : (
            <>
              <ExamHeader $viewMode={viewMode}>
                <ExamTitle $viewMode={viewMode}>{exam.title}</ExamTitle>
                {exam.description && (
                  <div style={{ 
                    fontSize: '15px', 
                    color: viewMode === 'print' ? '#333' : '#657786', 
                    textAlign: 'center',
                    marginTop: '8px'
                  }}>
                    {exam.description}
                  </div>
                )}
                {viewMode === 'print' ? (
                  // Print mode: Table format like Word
                  <div style={{ 
                    width: '100%', 
                    marginTop: '16px',
                    marginBottom: '16px',
                    border: '1px solid #000',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                  }}>
                    <div style={{ padding: '8px', borderRight: '1px solid #000' }}>
                      Name: _____________________
                    </div>
                    <div style={{ padding: '8px', borderRight: '1px solid #000' }}>
                      Class: _____________________
                    </div>
                    <div style={{ padding: '8px' }}>
                      Mark: _____________________
                    </div>
                  </div>
                ) : (
                  <ExamMeta>
                    <MetaItem>
                      <strong>⏱️ Thời gian:</strong> {exam.duration} phút
                    </MetaItem>
                    <MetaItem>
                      <strong>📊 Tổng điểm:</strong> {exam.total_marks}
                    </MetaItem>
                    <MetaItem>
                      <strong>📝 Số câu hỏi:</strong> {exam.exam_questions?.length || 0}
                    </MetaItem>
                    {exam.applicable_classes && (
                      <MetaItem>
                        <strong>🎓 Lớp:</strong> {exam.applicable_classes}
                      </MetaItem>
                    )}
                  </ExamMeta>
                )}
              </ExamHeader>

              <QuestionsList>
                {groupedExamQuestions.length > 0 ? (
                  viewMode === 'print' ? (
                    // Print mode: Word-like format
                    groupedExamQuestions.map((group, exerciseIdx) => {
                      const typeInfo = group.typeInfo || {};
                      const engTitle = typeInfo.eng_title || '';
                      const vnTitle = typeInfo.vn_title || '';
                      const headerText = `Exercise ${exerciseIdx + 1}: ${engTitle}${vnTitle ? ` (${vnTitle})` : ''}`;

                      return (
                        <div key={`exercise-${group.typeId}`} style={{ marginBottom: '24px', pageBreakInside: 'avoid' }}>
                          <h3 style={{ 
                            fontSize: '14px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            marginBottom: '12px',
                            color: '#000'
                          }}>
                            {headerText}
                          </h3>
                          <div>
                            {group.questions.map((eq: any) => 
                              renderPrintQuestion(eq, questionNumberMap.get(eq.question?.id) || 1)
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    // Normal mode: Original colorful format
                    groupedExamQuestions.map((group, exerciseIdx) => {
                      const typeInfo = group.typeInfo || {};
                      const engTitle = typeInfo.eng_title || '';
                      const vnTitle = typeInfo.vn_title || '';
                      const headerText = `Exercise ${exerciseIdx + 1}: ${engTitle}${vnTitle ? ` (${vnTitle})` : ''}`;

                      return (
                        <div key={`exercise-${group.typeId}`} style={{ marginBottom: '32px' }}>
                          <h3 style={{ 
                            color: '#0066CC',
                            fontSize: '20px',
                            fontWeight: 700,
                            marginBottom: '16px',
                            borderBottom: '2px solid #0066CC',
                            paddingBottom: '8px'
                          }}>
                            {headerText}
                          </h3>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {group.questions.map((eq: any, qIndex: number) => 
                              renderQuestion(eq, questionNumberMap.get(eq.question?.id) || qIndex + 1)
                            )}
                          </div>
                        </div>
                      );
                    })
                  )
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#657786' }}>
                    📋 Đề thi chưa có câu hỏi nào
                  </div>
                )}
              </QuestionsList>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ExamPreviewModal;
